import * as React from 'react';
import { get } from 'lodash-es';
import {
  useLocales,
  useConfigApp,
  useConfigUserFeedback,
  useImagePreviewUrl,
  useEntityData,
} from '@web-config-app/core-react';
import { EntityDetailError } from '@web-config-app/core';
import { PrimitiveControlFormField } from '../../primitive-control-form-field/primitive-control-form-field.component';
import {
  EntityFormControlProps,
  EntityFormControlOwnProps,
} from '../../../types/controls';
import { useLocalizedField } from '../../../hooks/use-localized-field/use-localized-field';
import { useEntityDetailsProps } from '../../../hooks/use-entity-details-props/use-entity-details-props';
import { getComposedSchemaPropertyPath } from '../../../utils/get-composed-schema-property-path/get-composed-schema-property-path';
import { composeParentPath } from '../../../utils/compose-parent-path/compose-parent-path';

export interface LocalizedEntityFormControlProps
  extends EntityFormControlOwnProps {
  renderControl: (props: EntityFormControlProps) => JSX.Element;
}

/**
 * Wrapper component for localized properties. It implements the same
 * renderControl, omitWrapperComponent, and wrapperComponent props as EntityFormControl
 * to allow for rendering logic to remain consistent between the
 * two components.
 */

export const LocalizedEntityFormControl: React.FC<
  React.PropsWithChildren<LocalizedEntityFormControlProps>
> = ({
  renderControl,
  omitWrapperComponent,
  wrapperComponent: WrapperComponent = PrimitiveControlFormField,
  ...props
}) => {
  const { data: defaultLocaleData } = props;
  const { currentLocale } = useLocales();
  const { defaultLocale } = useConfigApp();
  const entityData = useEntityData();
  const { formPath } = useEntityDetailsProps();
  const { additionalValidationErrors } = useConfigUserFeedback(
    (state) => state,
  );

  const { data: localizedData, setData: setLocalizedData } = useLocalizedField({
    // Since this will connect directly to the store, we need to construct the full
    // path including the formPath
    path: `${formPath}.${props.path}`,
    locale: currentLocale,
  });
  const isEditingLocalizedValue = currentLocale !== defaultLocale;

  const handleLocalizedChange = (_: string, value: any) => {
    setLocalizedData(value);
  };

  // Get localized validation errors for the current field and locale
  const localizedFieldErrors = React.useMemo(() => {
    if (!isEditingLocalizedValue) {
      return { inputStatus: undefined, statusMessage: undefined };
    }

    const localizedErrors =
      additionalValidationErrors['localized-fields-validation'] || [];

    const composedPath = getComposedSchemaPropertyPath(formPath, props.path);
    const fieldErrors = localizedErrors.filter((error) => {
      const exactPathMatches = error.path === props.path;
      const composedPathMatches = error.path === composedPath;

      // Check if fieldPath ends with our field name for nested paths
      const errorWithFieldPath = error as EntityDetailError & {
        fieldPath?: string;
      };
      const fieldPathMatches =
        errorWithFieldPath.fieldPath &&
        errorWithFieldPath.fieldPath.endsWith(`.${props.path}`);

      const localeMatches = error.locale === currentLocale;

      return (
        (exactPathMatches || composedPathMatches || fieldPathMatches) &&
        localeMatches
      );
    });

    if (fieldErrors.length > 0) {
      return {
        inputStatus: 'error' as const,
        statusMessage: fieldErrors[0].detail,
      };
    }

    return { inputStatus: undefined, statusMessage: undefined };
  }, [
    isEditingLocalizedValue,
    additionalValidationErrors,
    props.path,
    currentLocale,
    formPath,
  ]);

  /**
   * Build new props for the localized control with data & handleChange
   * routed to the localized value state.
   *
   * This means that the get/set for this control will be handled outside of the
   * normal EntityForm state management system and, instead, connects directly
   * with the entity data store.
   */
  const localizedControlProps = isEditingLocalizedValue
    ? {
        ...props,
        // inject the localized value for the control into the usual `data` prop
        data: localizedData,
        // override handleChange to route the localized value change to the appropriate store location
        handleChange: handleLocalizedChange,
        // apply localized validation errors to the field
        inputStatus: localizedFieldErrors.inputStatus,
        statusMessage: localizedFieldErrors.statusMessage,
      }
    : props;

  // Detect image asset controls so we can use the default-locale image URL in the default locale preview.
  const controlType = props.schema?.['x-entity-control'];
  const isImageAssetControl =
    controlType?.type === 'assetFile' &&
    controlType?.options?.file?.fileType === 'image';

  // preview URL for viewing default-locale image when editing a non-default locale
  const previewPath = React.useMemo(() => {
    const normalizedFormPath = formPath && formPath !== 'root' ? formPath : '';

    const basePropertyPath = isImageAssetControl
      ? props.path.replace(/[^.]+$/, 'url')
      : props.path;

    const fullPropertyPath = composeParentPath(
      normalizedFormPath,
      basePropertyPath,
    );

    return `domainEntityAttributes.${fullPropertyPath}`;
  }, [formPath, isImageAssetControl, props.path]);

  const previewUrlFromStore = useImagePreviewUrl(defaultLocale, previewPath);

  const defaultLocalePreviewData = React.useMemo(() => {
    if (!isEditingLocalizedValue) {
      return defaultLocaleData;
    }

    if (isImageAssetControl) {
      const attributes = entityData?.attributes;
      const defaultAttrs = attributes?.domainEntityAttributes ?? attributes;
      // Use formPath to navigate to the correct nested object and get the URL
      const nestedData = formPath ? get(defaultAttrs, formPath) : defaultAttrs;
      const defaultLocaleImageUrl = nestedData?.url;

      // use the blob preview URL (for newly uploaded default-locale images),
      // then fall back to the persisted default-locale URL, and finally the
      // raw default-locale field value.
      return previewUrlFromStore || defaultLocaleImageUrl || defaultLocaleData;
    }

    return previewUrlFromStore || defaultLocaleData;
  }, [
    isEditingLocalizedValue,
    isImageAssetControl,
    entityData,
    formPath,
    previewUrlFromStore,
    defaultLocaleData,
  ]);

  return omitWrapperComponent ? (
    <>{renderControl(localizedControlProps)}</>
  ) : (
    <WrapperComponent
      {...localizedControlProps}
      showDefaultLocale={isEditingLocalizedValue}
      defaultLocaleData={defaultLocalePreviewData}
      data={isEditingLocalizedValue ? localizedData : defaultLocaleData}
    >
      {renderControl(localizedControlProps)}
    </WrapperComponent>
  );
};
